import { format } from "date-fns";
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { toZonedTime } from "date-fns-tz";

import { useDeskBookingRequest } from "../../../../api/grpc/desk/useDeskBookingRequest";
import { useTimeZoneContext } from "../../../../lib/context/TimeZone/TimeZoneContext";
import { useBookingsContext } from "../../Context/BookingsContext";
import { useBookingsMapContext } from "../../BookingsMap/Context/BookingsMapContext";
import {
  useFormatReservationDate as formatReservationDate,
  forReservationDate,
} from "../../lib/datePickerHelper";
import { defineLocationPath } from "../../helpers/defineLocationPath";

import { MatchingDesk } from "../../../../api/grpc/desk/desk";
import { Button } from "../../../shared/Button/Button";
import { Icon } from "../../../shared/Icon/Icon";
import {
  attachHoursToDate,
  dateToHoursAndMinutes,
} from "../../lib/dateInputConvert";
import { PersonCredentials } from "../PersonCredentials/PersonCredentials";
import { UserModalDesks } from "./UserModalDesks";

interface Props {
  desks: MatchingDesk[];
  handleModalClose: () => void;
  name?: string;
  imageUrl?: string;
  email?: string;
  company?: string;
  personAccountId?: string;
}

export const UserInfo = ({
  name,
  imageUrl,
  email,
  company,
  desks,
  personAccountId,
  handleModalClose,
}: Props) => {
  const { findNearbyDesks } = useDeskBookingRequest();
  const { timeZone } = useTimeZoneContext();
  const { allDay } = useBookingsMapContext();
  const [pagePagination, setPagePagination] = useState(1);
  const [loading, setLoading] = useState(false);
  const [availableDesks, setAvailableDesks] = useState<MatchingDesk[]>([]);
  const [nextPage, setNextPage] = useState(1);

  const {
    handleMatchedDesks,
    day,
    reservDeskVariables: { startTime, endTime },
    matchedDesks,
    activeTagsFilter,
    location: { locationName },
    setHeaderInfo,
  } = useBookingsContext();

  useEffect(() => {
    if (desks.length <= 0) {
      setAvailableDesks([]);
      return;
    }

    const fetchNearbyDesks = async () => {
      try {
        setLoading(true);
        let start = attachHoursToDate(
          format(day, "yyyy-MM-dd"),
          startTime,
          timeZone
        );
        let end = attachHoursToDate(
          format(day, "yyyy-MM-dd"),
          endTime,
          timeZone,
          allDay
        );

        let fetchedDesks: MatchingDesk[] = [];
        let pageFromResponse = pagePagination;

        const { response } = await findNearbyDesks({
          customerID: "",
          accountID: personAccountId || "",
          startTime: start,
          endTime: end,
          locationID: desks[0]?.locationID || "",
          tagsFilter: activeTagsFilter,
          timeZone: timeZone,
          page: pageFromResponse,
        });

        pageFromResponse = response.nextPage;
        fetchedDesks = response.desks;

        if (response.desks.length <= 0 && response.nextPage > 0) {
          do {
            const { response } = await findNearbyDesks({
              customerID: "",
              accountID: personAccountId || "",
              startTime: start,
              endTime: end,
              locationID: desks[0]?.locationID || "",
              tagsFilter: activeTagsFilter,
              timeZone: timeZone,
              page: pageFromResponse,
            });

            fetchedDesks = response.desks;
            pageFromResponse = response.nextPage;
          } while (fetchedDesks.length <= 0 && pageFromResponse > 0);
        }

        setAvailableDesks(fetchedDesks);
        setNextPage(pageFromResponse);
      } catch (error: any) {
        toast.error(
          error?.message || "Error fetching desks, please try again!"
        );
        console.log(error);
        setAvailableDesks([]);
      } finally {
        setLoading(false);
      }
    };

    fetchNearbyDesks();
  }, [desks, personAccountId, startTime, endTime, activeTagsFilter, timeZone]);

  const handleNearbyDesks = async () => {
    try {
      setLoading(true);

      if (availableDesks.length > 0) {
        if (matchedDesks) {
          handleMatchedDesks([...matchedDesks, ...availableDesks]);
        } else {
          handleMatchedDesks(availableDesks);
        }
      }

      setPagePagination(nextPage);

      const startDate = new Date(
        attachHoursToDate(format(day, "yyyy-MM-dd"), startTime, timeZone)
      );
      const formattedDate = forReservationDate(
        toZonedTime(startDate, timeZone),
        timeZone
      );

      setHeaderInfo({
        day: formattedDate,
        location:
          locationName && locationName !== "RefreshedId"
            ? locationName
            : "All locations",
        time: `${dateToHoursAndMinutes(
          startDate,
          timeZone
        )}-${dateToHoursAndMinutes(
          new Date(
            attachHoursToDate(
              format(day, "yyyy-MM-dd"),
              endTime,
              timeZone,
              allDay
            )
          ),
          timeZone
        )}`,
      });

      handleModalClose();
    } catch (error: any) {
      toast.error(
        error?.message || "Error processing desks, please try again!"
      );
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="UserInfo w-full">
      <h3>Work near</h3>
      <PersonCredentials
        className="UserInfo__creds"
        imageUrl={imageUrl}
        name={name}
      />
      <h4>{name}</h4>
      {company && <span className="UserInfo__company">{company}</span>}
      <div className="UserInfo__email flex-a-center">
        <Icon icon="email-icon-empty" />
        <h6>{email}</h6>
      </div>
      <div className="UserInfo__divider"></div>
      {desks.length <= 0 ? (
        <div className="UserInfo__empty">
          <Icon icon="red-error-icon" />
          <span className="d-block">No results found.</span>
          <span>Please try adjusting your search.</span>
        </div>
      ) : (
        <>
          <span className="UserInfo__scheduled">
            Scheduled on-site day for {name}:
          </span>
          {desks.map((desk) => (
            <UserModalDesks
              key={desk.deskID}
              name={desk.deskName}
              reservationStartTime={dateToHoursAndMinutes(
                new Date(desk.event[0].startTime),
                timeZone
              )}
              reservationEndTime={dateToHoursAndMinutes(
                new Date(desk.event[0].endTime),
                timeZone
              )}
              createdTime={formatReservationDate(
                toZonedTime(new Date(desk.event[0].startTime), timeZone)
              )}
              locationPath={defineLocationPath(desk.locationPath)}
              allDay={desk.event[0].allDay}
            />
          ))}
          <div className="UserInfo__available flex-a-center">
            <span>Tentatively available desk</span>
            <span className="UserInfo__available--count flex-center">
              {availableDesks.length}
            </span>
          </div>
        </>
      )}
      <Button
        title="Find nearby desk"
        disabled={desks.length <= 0 || loading}
        color="business"
        size="medium"
        loadTitle="Finding..."
        loading={loading}
        loadSpinner={loading}
        onClick={handleNearbyDesks}
        className="UserInfo__find"
      />
    </div>
  );
};
